import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  CoreContextActionTypes,
  SetCookieTrackingConsent,
  SetCookieTrackingConsentHandled,
  SettingsLoaded,
} from './core-context-actions';
import { TrackingService } from '@app/core/tracking/tracking.service';
import { first, map, switchMap, tap } from 'rxjs/operators';
import { Action, select, Store } from '@ngrx/store';
import { MemberService } from '@app/core/members/member.service';
import { SettingsService } from '@app/core/settings/settings.service';
import { COOKIES_CONSENT_KEY } from '@app/core/cookie-consent/cookie-consent.service';
import { logMsg } from '@app/shared/utils/logger';
import * as fromRoot from '@app/core/store/reducers';
import { AutologinService } from '@app/core/store/core-context/autologin.service';

@Injectable()
export class CoreContextEffects {

  public loadSettings$ = createEffect(() => this.actions$.pipe(
    ofType(CoreContextActionTypes.LOAD_SETTINGS),
    map(() => this.settingsService.getSettingsFromMetaTag()),
    map((settings) => new SettingsLoaded(settings)),
  ));

  public setCookieTrackingConsent$ = createEffect(() => this.actions$.pipe(
    ofType(CoreContextActionTypes.SET_COOKIE_TRACKING_CONSENT),
    tap((a) => this.handleTrackingChange(a)),
    map(() => new SetCookieTrackingConsentHandled()),
  ));
  private schema$ = this.settingsService.getSettingsOnce().pipe(map((s) => s.schemaObject));

  constructor(private actions$: Actions,
              private store: Store<fromRoot.State>,
              private trackingService: TrackingService,
              private memberService: MemberService,
              private settingsService: SettingsService,
              private autologin: AutologinService) {}

  public handleTrackingChange(a: Action): void {
    logMsg(CoreContextEffects, 'handleTrackingChange');

    const action = <SetCookieTrackingConsent>a;
    this.trackingService.toggleTracing(action.enabled);

    if (action.updateMember) { this.updateConsent(action); }
  }

  private updateConsent(action: SetCookieTrackingConsent): void {
    this.store.pipe(
      select(fromRoot.selectMember),
      first(),
    ).subscribe((memberState) => {
      if (memberState.isMemberFetched) {
        this.updateMemberConsent(action.enabled);
      } else if (memberState.isBasicMemberFetched) {
        this.updateAutologgedMemberConsent(action.enabled);
      }
    });
  }

  /**
   * Updates member using normal flow.
   * @param status - cookie_tracking consent status
   */
  private updateMemberConsent(status: boolean): void {
    this.schema$.subscribe((schema) => {
      if (!schema.consents.availableConsentsKeys().includes(COOKIES_CONSENT_KEY)) {
        return;
      }
      const consent = { status: status };
      const form = {
        member_form: {
          consents: { [COOKIES_CONSENT_KEY]: consent },
        },
      };

      this.memberService.updateConsents(form).subscribe();
    });
  }

  /**
   * Updates consents only using autologin authentication.
   * @param status - cookie_tracking consent status
   */
  private updateAutologgedMemberConsent(status: boolean): void {
    this.autologin.updateCookieTracking(status)
      .subscribe();
  }
}
